import { useQuery } from '@tanstack/react-query'
import { UNIFIED_DOORS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import { useMemo } from 'react'
import useApiService from 'services/api'

/**
 * Restaurant doors (DoorsTableComponentV2) data source — reads the CRM-reports
 * read-model (`reports/doors/` + `reports/doors/summary/`).
 *
 * The door analog of `useUnifiedChainsTable`. The restaurant-doors table is the
 * one door table that does NOT use `DECommonTable` — it has a bespoke component
 * (`DoorsTableComponentV2`) with elastic name/menu search, a product selector,
 * a rich LTV summary header (`total_ltv`, `places_count`), and a wide column set
 * (`DoorsTableColumnsV2`). So instead of the generic `useUnifiedDoorsTableActions`
 * hook, this returns the pieces the bespoke component needs:
 *
 *  - `listAction`: a bound list fetcher with a
 *    `(params, signal) => Promise<{ results }>` shape. The `reports/doors/`
 *    endpoint already returns rows in the shape the doors columns, selection, and
 *    row actions expect (nested `restaurant`, `restaurant.firstbite_id`, a
 *    top-level `door_proxy_id`, and `chain_proxy.id`), so the rows are returned
 *    as-is.
 *  - `unifiedCount`: the reports door count for the current filters, to drive
 *    pagination + `setTotalRowsInBackend` so paging matches the rows.
 *
 * (The `unified*` names on the return object are kept for a stable seam with the
 * consuming component; the data now comes from the reports read-model.)
 */

type ApiService = ReturnType<typeof useApiService>

/**
 * Fetch one page of restaurant doors from the `reports/doors/` endpoint. The
 * reports rows already carry the shape the restaurant-doors consumers read
 * (door detail nested under `restaurant`, `restaurant.firstbite_id`, a top-level
 * `door_proxy_id`), so they are returned unchanged. Shared by
 * `useUnifiedDoorsReportsTable` (the doors target table) and `MobileDoorsListPage`.
 */
export async function fetchUnifiedRestaurantDoors(
  api: ApiService,
  params: Record<string, any>,
  signal?: AbortSignal
): Promise<{ results: any[] }> {
  const res = await api.getDoorsWithCRMReports(params, signal)
  return { results: res.results } as { results: any[] }
}

export function useUnifiedDoorsReportsTable(
  filterParams: Record<string, any>,
  // Gate for the summary query (doors tab active or a chain filter present).
  // Prevents the summary from firing/forwarding stats for a tab the user
  // isn't viewing.
  summaryEnabled = true
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: any[] }>
  unifiedCount: number | undefined
  unifiedChainsCount: number | undefined
  unifiedLtv: number | undefined
  unifiedSummaryFetching: boolean
} {
  const api = useApiService()

  const listAction = useMemo(() => {
    return (params: Record<string, any>, signal?: AbortSignal) =>
      fetchUnifiedRestaurantDoors(api, params, signal)
  }, [api])

  const { data: doorsSummary, isFetching: unifiedSummaryFetching } = useQuery({
    // Use the default (finite, 0) staleTime so the count refetches after
    // mutations that invalidate UNIFIED_DOORS_SUMMARY_KEY. Do NOT set
    // `staleTime: Infinity` here: an infinite staleTime would leave the
    // count stale until a hard reload.
    enabled: summaryEnabled,
    queryKey: [UNIFIED_DOORS_SUMMARY_KEY, filterParams],
    queryFn: async ({ signal }) => {
      const res = await api.getDoorReportsSummary(filterParams, signal)
      return (
        res?.results?.[0] ?? {
          places_count: 0,
          chains_count: 0,
          total_ltv: 0,
        }
      )
    },
  })

  return {
    listAction,
    // The full stat-card set for the filtered doors, all from the reports doors
    // summary. See DoorsTableComponentV2.
    unifiedCount: doorsSummary?.places_count,
    unifiedChainsCount: doorsSummary?.chains_count,
    unifiedLtv: doorsSummary?.total_ltv,
    unifiedSummaryFetching,
  }
}
